import { WarningBeforeRecording } from '@carboncopyco/components';
import { TextBase } from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { openLinkInBrowser } from '../../../util/path-util';
import { DONT_SHOW_KEY } from '../../constants';
import { updateDontShowThisAgain } from '../../shared/utils';

const IS_SHARING_MODAL_ENABLED = 'IS_SHARING_MODAL_ENABLED';
const isWarningBeforeRecordingDisabled = localStorage.getItem(
  IS_SHARING_MODAL_ENABLED
);

interface TypeURLInputProps {
  onKeyUp: (data: any) => void;
  onUrlChanged: (data: any) => void;
  startNewRecording: () => void;
  isAlert: boolean;
  isButtonEnabled: boolean;
  url: string;
}

export const TypeURLInput = ({
  onKeyUp,
  onUrlChanged,
  isAlert = false,
  isButtonEnabled = true,
  startNewRecording,
  url,
}: TypeURLInputProps) => {
  const [isWarningBeforeRecordingOpen, setIsWarningBeforeRecordingOpen] =
    useState(false);
  const [isWarningModalChecked, setIsWarningModalChecked] = useState(false);
  const warningBeforeRecordingRef = useRef<any>();
  const { trackEvent } = useAnalytics();

  const onURLSubmit = () => {
    const isInvalidSubmit = !url || !isButtonEnabled || isAlert;
    if (isInvalidSubmit) {
      return;
    }
    const dontShowThisAgainArr = JSON.parse(
      localStorage.getItem(DONT_SHOW_KEY) || '[]'
    );
    const dontShowThisAgain = dontShowThisAgainArr.indexOf('record') > -1;

    if (dontShowThisAgain || isWarningBeforeRecordingDisabled === 'true') {
      startNewRecording();
    } else {
      setIsWarningBeforeRecordingOpen(true);
    }
  };

  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (isButtonEnabled && url && e.key === 'Enter') onURLSubmit();
    onKeyUp(e);
  };

  const handleLearnMore = () => {
    openLinkInBrowser(null, 'https://help.demostack.com/en/articles/6202302');
    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_LEARN_MORE, {
      location: 'new_clone',
    });
  };
  const handleAcceptTerms = () => {
    trackEvent(AnalyticsTrackEventName.CLONE_CLICK_GOT_IT, {
      location: 'new_clone',
    });
    startNewRecording();
  };

  useEffect(() => {
    if (isWarningBeforeRecordingDisabled !== 'true') {
      const element = document.getElementById('acceptButton');
      element?.focus();
    }
  }, [isWarningBeforeRecordingOpen]);

  return (
    <div className="flex items-center text-center justify-center w-full">
      <div
        className={`w-440 flex items-center rounded-3xl pl-2.5 pr-1 h-9 border w-full  ${
          isAlert ? 'border-ds-danger-300' : 'border-ds-natural-150'
        }`}
      >
        <input
          autoFocus
          className="bg-transparent p-0 w-full px-3 mr-2.5 text-sm outline-none"
          placeholder="Type URL here"
          value={url}
          onKeyUp={handleKeyUp}
          onChange={(event) => onUrlChanged?.(event.target.value)}
        />
        {url && isButtonEnabled ? (
          <div>
            <button
              onClick={onURLSubmit}
              className="bg-ds-accent2-300 rounded-3xl px-4 py-0.5 hover:bg-ds-accent2-400 active:bg-ds-accent2-500"
            >
              <TextBase
                label="GO"
                color="text-ds-natural-600"
              />
            </button>
            <div className="text-left">
              <div ref={warningBeforeRecordingRef} />
            </div>
          </div>
        ) : (
          <button className="bg-ds-natural-200-a rounded-3xl px-4 py-0.5 ">
            <TextBase
              label="GO"
              color="text-ds-natural-300"
            />
          </button>
        )}
        {url &&
          isButtonEnabled &&
          isWarningBeforeRecordingDisabled !== 'true' && (
            <WarningBeforeRecording
              isVisible={isWarningBeforeRecordingOpen}
              anchorRef={warningBeforeRecordingRef}
              onLearnMoreClick={handleLearnMore}
              onClose={() => {
                setIsWarningBeforeRecordingOpen(false);
              }}
              onAcceptTermAndStartCloning={handleAcceptTerms}
              isChecked={isWarningModalChecked}
              setIsChecked={(checked) => {
                updateDontShowThisAgain(checked, DONT_SHOW_KEY);

                setIsWarningModalChecked(checked);
                localStorage.setItem(
                  IS_SHARING_MODAL_ENABLED,
                  checked.toString()
                );
              }}
            />
          )}
      </div>
    </div>
  );
};
